import { EliteLandingPageState, type EliteOnboardingStatusResponse } from '../../types/misc.types'
import { hasEliteConsentText } from '../../components/elite/EliteConsentTextContent'
import { convertEpochToDate } from '../../utils/utils'
import {
  elitePlanSupportLearnMoreLinkHtml,
  resolvePlanId,
  updateCtaWithSelectedPlanId
} from '../new-elite-landing/components/NewEliteLandingPlanSelector'
import type { PlanConfig } from './types/plan.types'
import type { ElitePlansLandingStateConfig, ElitePlansLifecycleUi } from './types/lifecycle.types'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from './constants/planIds'
export type { ElitePlansLandingStateConfig, ElitePlansLifecycleUi } from './types/lifecycle.types'

/** UI copy keyed by backend `eliteLandingPageState`. */
export const elitePlansLandingStateConfig: Partial<Record<`${EliteLandingPageState}`, ElitePlansLandingStateConfig>> = {
  [EliteLandingPageState.ONB_NOT_INITIATED]: {
    showConsent: true
  },
  [EliteLandingPageState.PAYMENT_PENDING]: {
    isCtaDisabled: true,
    supportText:
      'Awaiting payment confirmation from bank. If payment fails, your amount will be refunded in 3 working days.'
  },
  [EliteLandingPageState.PAYMENT_FAILED]: {
    supportText:
      'Your last payment failed. Any amount deducted will be refunded in 3 working days. You can try making the payment again.'
  },
  [EliteLandingPageState.RE_INITIATE_PAYMENT]: {
    supportText:
      'Your last payment failed. Any amount deducted will be refunded in 3 working days. You can try making the payment again.'
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING]: {
    ctaLabel: 'Complete e-Sign',
    supportText:
      'Your payment was successful. Complete eSign now to activate your {membershipLabel}.' +
      elitePlanSupportLearnMoreLinkHtml
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING]: {
    ctaLabel: 'Complete e-Sign',
    supportText:
      'Your payment was successful. Complete eSign before {activationExpiryDate} to activate your {membershipLabel}.' +
      elitePlanSupportLearnMoreLinkHtml
  },
  [EliteLandingPageState.REFUND_DUE_TO_ESIGN_PENDING]: {
    supportText:
      "{membershipLabel} could not be activated since eSign wasn't completed. Your amount will be refunded within 3-5 working days."
  },
  [EliteLandingPageState.PLAN_EXPIRED]: {
    ctaLabel: 'Renew {planName}'
  },
  // Primary not Elite/Prime — family secondary cannot buy; CTA stays disabled (same as familyEliteLandingPageStateConfig)
  [EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED]: {
    isCtaDisabled: true,
    ctaLabel: 'Upgrade to Elite'
  },
  // Elite paid but eSign pending: Prime CTA stays tappable and opens the "complete Elite eSign first" sheet (hero)
  [EliteLandingPageState.ELITE_PAYMENT_COMPLETED_PRIME_PENDING]: {
    clearLiveCta: true
  }
}

function resolvePlanCopy(plan: PlanConfig) {
  if (plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID) {
    return {
      planName: 'Elite Prime',
      membershipLabel: 'Elite Prime membership'
    }
  }

  return {
    planName: 'your plan',
    membershipLabel: 'membership'
  }
}

function applyPlanCopy(text: string | undefined, plan: PlanConfig): string | undefined {
  if (!text) {
    return undefined
  }

  const { planName, membershipLabel } = resolvePlanCopy(plan)

  return text.replaceAll('{planName}', planName).replaceAll('{membershipLabel}', membershipLabel)
}

function resolveLifecycleCopy(
  text: string | undefined,
  plan: PlanConfig,
  activationExpiryDate?: number
): string | undefined {
  const withPlanCopy = applyPlanCopy(text, plan)
  if (!withPlanCopy) {
    return undefined
  }
  if (!withPlanCopy.includes('{activationExpiryDate}')) {
    return withPlanCopy
  }

  return withPlanCopy.replace('{activationExpiryDate}', convertEpochToDate(activationExpiryDate ?? 0, '%d %b'))
}

function resolveEligiblePlanId(status?: EliteOnboardingStatusResponse): string {
  const plans = status?.eligiblePlans ?? []
  const recommended = plans.find((plan) => plan?.recommended)
  return resolvePlanId(recommended ?? plans[0])
}

export function resolveElitePlansLifecycleUi({
  plan,
  status
}: {
  plan: PlanConfig
  status?: EliteOnboardingStatusResponse
}): ElitePlansLifecycleUi {
  const landingPageState = status?.eliteLandingPageState
  const stateConfig = landingPageState ? (elitePlansLandingStateConfig[landingPageState] ?? {}) : {}

  const { showConsent, consentTextList } = resolveLifecycleConsent(plan, status, stateConfig)

  const configuredCtaLabel = resolveLifecycleCopy(stateConfig.ctaLabel, plan)
  const defaultCtaLabel = plan.cta.label ?? ''

  return {
    landingPageState,
    ctaLabel: configuredCtaLabel ?? defaultCtaLabel,
    isCtaDisabledByState: Boolean(stateConfig.isCtaDisabled),
    showConsent,
    consentTextList,
    supportText: resolveLifecycleCopy(stateConfig.supportText, plan, status?.subscriptionDetails?.activationExpiryDate),
    liveCta: resolveLifecycleLiveCta(status, stateConfig)
  }
}

function resolveLifecycleConsent(
  plan: PlanConfig,
  status: EliteOnboardingStatusResponse | undefined,
  stateConfig: ElitePlansLandingStateConfig
) {
  if (plan.id === ELITE_PLANS_ELITE_PRIME_TAB_ID) {
    // Prime consent is driven only by prime-status `isPrimeConsent`, independent of landing page state
    const primeConsentTextList = status?.consentTextList ?? []
    return {
      consentTextList: primeConsentTextList,
      showConsent: Boolean(status?.isPrimeConsent) && hasEliteConsentText(primeConsentTextList)
    }
  }

  const consentFromStatus = status?.consentTextList ?? []
  const consentFromContent = plan.consentTextList ?? []
  const consentTextList = consentFromStatus.length > 0 ? consentFromStatus : consentFromContent
  const showConsentByState =
    stateConfig.showConsent === true ||
    (!status?.eliteLandingPageState && hasEliteConsentText(consentTextList))

  return {
    consentTextList,
    showConsent: showConsentByState && hasEliteConsentText(consentTextList)
  }
}

/** PLAN_EXPIRED posts the renewal CTA from the tab status payload (prime included). */
function resolveLifecycleLiveCta(
  status: EliteOnboardingStatusResponse | undefined,
  stateConfig: ElitePlansLandingStateConfig
): EliteOnboardingStatusResponse['cta'] | undefined {
  if (stateConfig.clearLiveCta) return undefined
  if (status?.eliteLandingPageState === EliteLandingPageState.PLAN_EXPIRED) {
    return status.renewal?.cta
  }
  return status?.cta
}

export function buildElitePlansCtaPayload(
  status: EliteOnboardingStatusResponse | undefined,
  liveCta: EliteOnboardingStatusResponse['cta'] | undefined
): Record<string, unknown> | null {
  if (!liveCta) return null
  const planId = resolveEligiblePlanId(status)
  if (!planId) {
    return { ...(liveCta ?? {}) }
  }
  return updateCtaWithSelectedPlanId(liveCta, planId)
}
